import { isThisISOWeek, isThisYear, isToday, isTomorrow } from 'date-fns';
import type { TFunctionNonStrict } from 'i18next';
import type { DayOfWeek, OpeningHoursSpecification } from 'schema-dts';
import type { PartnerBusinessHourDescriptor } from '../redux/ducks/SearchDucks.types';

export const formatPartnerRouteName = (name: string) => {
  const input = name
    .replace(/[^a-zA-Z0-9\ ]/g, '')
    .replace('  ', ' ')
    .replace('  ', ' ')
    .toLowerCase()
    .trim();
  const parts: string[] = input.split(' ');
  if (parts.length <= 1) {
    return input;
  }

  let result = parts[0].toLowerCase().trim();
  for (let index = 1; index < Math.min(3, parts.length); index++) {
    result = `${result}-${parts[index].toLowerCase().trim()}`;
  }

  return result;
};

export const formatDay = (options: {
  timestamp: number;
  timezone: string;
  language: string;
  t: TFunctionNonStrict<'translation', undefined>;
  shortDay?: boolean;
  shortMonth?: boolean;
  noYear?: boolean;
}): string => {
  const messageDate = new Date(options.timestamp);

  if (isToday(messageDate)) {
    return options.t('static.generic.today');
  }
  if (isTomorrow(messageDate)) {
    return options.t('static.generic.tomorrow');
  }
  if (isThisISOWeek(messageDate)) {
    return options.t('static.generic.this_week_day', {
      name: Intl.DateTimeFormat(options.language, {
        weekday: options?.shortDay ? 'short' : 'long',
        timeZone: options.timezone,
      }).format(messageDate),
    });
  }
  if (isThisYear(messageDate)) {
    return Intl.DateTimeFormat(options.language, {
      weekday: options?.shortDay ? 'short' : 'long',
      day: 'numeric',
      month: options?.shortMonth ? 'short' : 'long',
      timeZone: options.timezone,
    }).format(new Date(messageDate));
  }
  return Intl.DateTimeFormat(options.language, {
    weekday: options?.shortDay ? 'short' : 'long',
    day: 'numeric',
    month: options?.shortMonth ? 'short' : 'long',
    year: options?.noYear ? undefined : 'numeric',
    timeZone: options.timezone,
  }).format(new Date(messageDate));
};

export const formatHourToSchemaMarkupOpeningHourSpecification = (
  hour: PartnerBusinessHourDescriptor,
): OpeningHoursSpecification => {
  const weekDays: DayOfWeek[] = [
    'https://schema.org/Monday',
    'https://schema.org/Tuesday',
    'https://schema.org/Wednesday',
    'https://schema.org/Thursday',
    'https://schema.org/Friday',
    'https://schema.org/Saturday',
    'https://schema.org/Sunday',
  ];
  const hourDay =
    weekDays[Math.min(weekDays.length - 1, Math.max(0, hour.day - 1))];

  const startMinutes = `${Math.floor(hour.start / 60) % 60}`.padStart(2, '0');
  const startHours = `${Math.floor(hour.start / 3600)}`.padStart(2, '0');
  const startTime = `${startHours}:${startMinutes}`;

  const endMinutes = `${Math.floor(hour.end / 60) % 60}`.padStart(2, '0');
  const endHours = `${Math.floor(hour.end / 3600)}`.padStart(2, '0');
  const endTime = `${endHours}:${endMinutes}`;

  return {
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: [hourDay],
    opens: startTime,
    closes: endTime,
  };
};
